import { useCallback, useMemo, useRef } from "react";
import style from "./style.module.css";
import {
  GradientValue,
  GradientStop,
  LinearGradient,
  RadialGradient
} from "lib/gradients/gradientTypes";
// Note: we intentionally avoid updateStop here because it sorts stops on every
// call, which causes jumps and stuck behavior when dragging a stop past another.
import {
  angleToEndpoints,
  endpointsToAngle,
  centerToPosition,
  positionToCenter,
  pointAlongLine,
  projectPointOntoLine,
  computeRadialRadius
} from "./gradientGeometry";
import { Bounds, Point } from "./gradientGeometry";
import GradientHandle from "./GradientHandle";
import GradientLine from "./GradientLine";

interface GradientOverlayProps {
  gradient: GradientValue;
  width: number;
  height: number;
  onChange: (gradient: GradientValue) => void;
  selectedStopId?: string | null;
  onSelectStop?: (stopId: string) => void;
}

const GradientOverlay = ({
  gradient,
  width,
  height,
  onChange,
  selectedStopId,
  onSelectStop
}: GradientOverlayProps) => {
  const bounds: Bounds = useMemo(() => ({ width, height }), [width, height]);

  if (gradient.type === "linear") {
    return (
      <LinearGradientOverlay
        gradient={gradient}
        bounds={bounds}
        width={width}
        height={height}
        onChange={onChange}
        selectedStopId={selectedStopId}
        onSelectStop={onSelectStop}
      />
    );
  }

  return (
    <RadialGradientOverlay
      gradient={gradient}
      bounds={bounds}
      width={width}
      height={height}
      onChange={onChange}
      selectedStopId={selectedStopId}
      onSelectStop={onSelectStop}
    />
  );
};

interface LinearOverlayProps {
  gradient: LinearGradient;
  bounds: Bounds;
  width: number;
  height: number;
  onChange: (gradient: GradientValue) => void;
  selectedStopId?: string | null;
  onSelectStop?: (stopId: string) => void;
}

const LinearGradientOverlay = ({
  gradient,
  bounds,
  width,
  height,
  onChange,
  selectedStopId,
  onSelectStop
}: LinearOverlayProps) => {
  const gradientRef = useRef(gradient);
  gradientRef.current = gradient;

  // start/end = inner endpoints (where 0%/100% stops sit, within bounds)
  // extStart/extEnd = extended endpoints (line drawn here, endpoint handles here)
  const { start, end, extStart, extEnd } = useMemo(
    () => angleToEndpoints(gradient.angle, bounds),
    [gradient.angle, bounds]
  );

  const handleStartDrag = useCallback(
    (x: number, y: number) => {
      const newAngle = endpointsToAngle({ x, y }, end);
      const updated = { ...gradientRef.current, angle: newAngle };
      gradientRef.current = updated;
      onChange(updated);
    },
    [end, onChange]
  );

  const handleEndDrag = useCallback(
    (x: number, y: number) => {
      const newAngle = endpointsToAngle(start, { x, y });
      const updated = { ...gradientRef.current, angle: newAngle };
      gradientRef.current = updated;
      onChange(updated);
    },
    [start, onChange]
  );

  const handleStopDrag = useCallback(
    (stopId: string, x: number, y: number) => {
      onSelectStop?.(stopId);
      const newPosition = projectPointOntoLine({ x, y }, start, end);
      const current = gradientRef.current;
      const updated = {
        ...current,
        stops: current.stops.map(s =>
          s.id === stopId ? { ...s, position: newPosition } : s
        )
      };
      gradientRef.current = updated;
      onChange(updated);
    },
    [start, end, onChange, onSelectStop]
  );

  const handleStopClick = useCallback(
    (stopId: string) => {
      onSelectStop?.(stopId);
    },
    [onSelectStop]
  );

  // SVG needs extra space since the extended line goes beyond bounds
  const padding = 40;
  const svgWidth = width + padding * 2;
  const svgHeight = height + padding * 2;

  return (
    <div
      className={style.overlay}
      style={{
        width: svgWidth,
        height: svgHeight,
        left: -padding,
        top: -padding
      }}
    >
      <svg
        className={style.overlaySvg}
        width={svgWidth}
        height={svgHeight}
        data-gradient-overlay="true"
        data-offset-x={padding}
        data-offset-y={padding}
      >
        <g transform={`translate(${padding}, ${padding})`}>
          {/* Extended line between extStart and extEnd (drawn beyond element box) */}
          <GradientLine
            x1={extStart.x}
            y1={extStart.y}
            x2={extEnd.x}
            y2={extEnd.y}
          />

          {/* Endpoint handles for changing angle (at extended positions, outside element) */}
          <GradientHandle
            x={extStart.x}
            y={extStart.y}
            onDrag={handleStartDrag}
            size={10}
          />
          <GradientHandle
            x={extEnd.x}
            y={extEnd.y}
            onDrag={handleEndDrag}
            size={10}
          />

          {/* Stop handles along the inner line (within bounds) */}
          {gradient.stops.map(stop => {
            const pos = pointAlongLine(start, end, stop.position);
            return (
              <StopHandle
                key={stop.id}
                stop={stop}
                x={pos.x}
                y={pos.y}
                lineStart={start}
                lineEnd={end}
                selected={stop.id === selectedStopId}
                onDrag={handleStopDrag}
                onClick={handleStopClick}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
};

interface StopHandleProps {
  stop: GradientStop;
  x: number;
  y: number;
  lineStart: Point;
  lineEnd: Point;
  selected: boolean;
  onDrag: (stopId: string, x: number, y: number) => void;
  onClick: (stopId: string) => void;
}

const StopHandle = ({
  stop,
  x,
  y,
  onDrag,
  onClick,
  selected
}: StopHandleProps) => {
  const handleDrag = useCallback(
    (dragX: number, dragY: number) => {
      onDrag(stop.id, dragX, dragY);
    },
    [stop.id, onDrag]
  );

  const handleClick = useCallback(() => {
    onClick(stop.id);
  }, [stop.id, onClick]);

  return (
    <GradientHandle
      x={x}
      y={y}
      onDrag={handleDrag}
      onClick={handleClick}
      color={stop.color}
      size={14}
      selected={selected}
    />
  );
};

interface RadialOverlayProps {
  gradient: RadialGradient;
  bounds: Bounds;
  width: number;
  height: number;
  onChange: (gradient: GradientValue) => void;
  selectedStopId?: string | null;
  onSelectStop?: (stopId: string) => void;
}

const RadialGradientOverlay = ({
  gradient,
  bounds,
  width,
  height,
  onChange,
  selectedStopId,
  onSelectStop
}: RadialOverlayProps) => {
  const gradientRef = useRef(gradient);
  gradientRef.current = gradient;

  const center = useMemo(
    () => positionToCenter(gradient.centerX, gradient.centerY, bounds),
    [gradient.centerX, gradient.centerY, bounds]
  );

  // Compute the radius for the ring (distance to farthest corner)
  const ringRadius = useMemo(
    () => computeRadialRadius(center, bounds),
    [center, bounds]
  );

  // Radial stop line extends from center toward the furthest edge
  const radiusEnd = useMemo((): Point => {
    const distRight = width - center.x;
    const distLeft = center.x;
    const distBottom = height - center.y;
    const distTop = center.y;
    const maxDist = Math.max(distRight, distLeft, distBottom, distTop);

    if (maxDist === distRight) return { x: width, y: center.y };
    if (maxDist === distLeft) return { x: 0, y: center.y };
    if (maxDist === distBottom) return { x: center.x, y: height };
    return { x: center.x, y: 0 };
  }, [center, width, height]);

  const handleCenterDrag = useCallback(
    (x: number, y: number) => {
      const clampedX = Math.max(0, Math.min(width, x));
      const clampedY = Math.max(0, Math.min(height, y));
      const pos = centerToPosition({ x: clampedX, y: clampedY }, bounds);
      const updated = {
        ...gradientRef.current,
        centerX: Math.round(pos.x),
        centerY: Math.round(pos.y)
      };
      gradientRef.current = updated;
      onChange(updated);
    },
    [bounds, width, height, onChange]
  );

  const handleStopDrag = useCallback(
    (stopId: string, x: number, y: number) => {
      onSelectStop?.(stopId);
      const newPosition = projectPointOntoLine({ x, y }, center, radiusEnd);
      const current = gradientRef.current;
      const updated = {
        ...current,
        stops: current.stops.map(s =>
          s.id === stopId ? { ...s, position: newPosition } : s
        )
      };
      gradientRef.current = updated;
      onChange(updated);
    },
    [center, radiusEnd, onChange, onSelectStop]
  );

  const handleStopClick = useCallback(
    (stopId: string) => {
      onSelectStop?.(stopId);
    },
    [onSelectStop]
  );

  // Extra padding so the ring can extend beyond the element bounds
  const ringOverflow = Math.ceil(
    Math.max(
      ringRadius - center.x,
      ringRadius - (width - center.x),
      ringRadius - center.y,
      ringRadius - (height - center.y),
      0
    )
  );
  const padding = ringOverflow + 20;
  const svgWidth = width + padding * 2;
  const svgHeight = height + padding * 2;

  return (
    <div
      className={style.overlay}
      style={{
        width: svgWidth,
        height: svgHeight,
        left: -padding,
        top: -padding
      }}
    >
      <svg
        className={style.overlaySvg}
        width={svgWidth}
        height={svgHeight}
        data-gradient-overlay="true"
        data-offset-x={padding}
        data-offset-y={padding}
      >
        <g transform={`translate(${padding}, ${padding})`}>
          {/* Radial extent ring */}
          <circle
            cx={center.x}
            cy={center.y}
            r={ringRadius}
            fill="none"
            stroke="rgba(255, 255, 255, 0.5)"
            strokeWidth={1.5}
            className={style.radialRing}
          />
          <circle
            cx={center.x}
            cy={center.y}
            r={ringRadius}
            fill="none"
            stroke="rgba(0, 0, 0, 0.1)"
            strokeWidth={0.5}
            className={style.radialRing}
          />

          {/* Line from center to edge showing radius direction */}
          <GradientLine
            x1={center.x}
            y1={center.y}
            x2={radiusEnd.x}
            y2={radiusEnd.y}
          />

          {/* Center handle (drag to move gradient center) */}
          <GradientHandle
            x={center.x}
            y={center.y}
            onDrag={handleCenterDrag}
            size={10}
          />

          {/* Stop handles along the radius line */}
          {gradient.stops.map(stop => {
            const pos = pointAlongLine(center, radiusEnd, stop.position);
            return (
              <StopHandle
                key={stop.id}
                stop={stop}
                x={pos.x}
                y={pos.y}
                lineStart={center}
                lineEnd={radiusEnd}
                selected={stop.id === selectedStopId}
                onDrag={handleStopDrag}
                onClick={handleStopClick}
              />
            );
          })}
        </g>
      </svg>
    </div>
  );
};

export default GradientOverlay;
